import { ArrowUpDown, Folder, ListOrdered, Pencil, Play } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { roleAtLeast } from '../../shared/audience';
import { practiceEventId } from '../../shared/program';
import type { CollectionEntryDTO, SongListItemDTO } from '../../shared/dto/library';
import { ApiFailure } from '../api/client';
import { useCollection, useSongs } from '../api/library';
import { useMe } from '../auth/AuthProvider';
import { Page } from '../components/layout';
import { Button, Card, EmptyState } from '../components/ui';
import { useT } from '../i18n';
import { canSaveOffline } from '../offline/hooks';
import { InstallForOfflineBanner } from '../offline/InstallForOfflineBanner';
import { PackButton, SongOfflineAction } from '../offline/SaveOfflineButton';
import { QueryStatus } from './library/QueryStatus';
import { ReorderSheet } from './library/ReorderSheet';
import { SongRow } from './library/SongRow';

interface Item {
  entry: CollectionEntryDTO;
  /** Index into CollectionDTO.entries (ordered by position) — the `i` of the song URL. */
  index: number;
  song: SongListItemDTO;
}

/**
 * /collections/:collectionId — ordered setlist or folder; songs open with setlist context
 * (?c=&i=&e=: `e`, the entry id, survives a reorder; `i` is the fallback for older links).
 * Editors and admins (the conductor has role editor) set the shared order with "Raða". A practice's
 * list (p-<eventId>) has no screen of its own: its event page shows the songs.
 */
export default function CollectionScreen() {
  const { collectionId } = useParams();
  const eventId = collectionId ? practiceEventId(collectionId) : null;
  if (eventId) return <Navigate to={`/events/${encodeURIComponent(eventId)}`} replace />;
  return <CollectionBody collectionId={collectionId} />;
}

function CollectionBody({ collectionId }: { collectionId: string | undefined }) {
  const t = useT();
  const me = useMe();
  const navigate = useNavigate();
  // The order is shared and may have just changed: always ask again when the screen opens.
  const colQ = useCollection(collectionId, { refetchOnMount: 'always' });
  const songsQ = useSongs();
  const col = colQ.data;
  const isEditor = !!me && roleAtLeast(me.role, 'editor');
  const [reordering, setReordering] = useState(false);

  const items = useMemo<Item[] | null>(() => {
    if (!col || !songsQ.data) return null;
    const byId = new Map(songsQ.data.map((s) => [s.id, s]));
    const ordered = [...col.entries].sort((a, b) => a.position - b.position);
    const out: Item[] = [];
    ordered.forEach((entry, index) => {
      const song = byId.get(entry.songId);
      if (song) out.push({ entry, index, song });
    });
    return out;
  }, [col, songsQ.data]);

  const hrefFor = (it: Item) =>
    `/songs/${it.song.id}?c=${encodeURIComponent(col!.id)}&i=${it.index}&e=${encodeURIComponent(it.entry.entryId)}`;
  const backTo = `/songs?tab=${col?.kind === 'folder' ? 'folders' : 'setlists'}`;

  if (!col) {
    const missing = colQ.error instanceof ApiFailure && colQ.error.status === 404;
    return (
      <Page title={t('library.setlist')} back="/songs">
        {missing ? (
          <EmptyState icon={ListOrdered} title={t('library.collection_not_found')} />
        ) : (
          <QueryStatus q={colQ} />
        )}
      </Page>
    );
  }

  const KindIcon = col.kind === 'folder' ? Folder : ListOrdered;
  const first = items?.[0];
  const offlineOk = canSaveOffline();

  return (
    <Page
      title={col.name}
      back={backTo}
      actions={
        isEditor ? (
          <>
            {col.entries.length > 1 ? (
              <Button variant="secondary" icon={ArrowUpDown} onClick={() => setReordering(true)} aria-label={t('library.reorder_songs')}>
                <span className="hidden sm:inline">{t('library.reorder')}</span>
              </Button>
            ) : null}
            <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/admin/library/collections/${col.id}`)} aria-label={t('library.edit_list')}>
              <span className="hidden sm:inline">{t('library.edit_list')}</span>
            </Button>
          </>
        ) : null
      }
    >
      <div className="flex flex-col gap-5">
        <Card className="flex flex-col gap-4 p-5 md:flex-row md:items-center">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--accent-soft)] text-[var(--accent)]">
              <KindIcon className="size-7" aria-hidden />
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-[var(--muted)]">
                {t(col.kind === 'folder' ? 'library.folder' : 'library.setlist')}
                {items ? ` · ${t('library.song_count', { count: items.length })}` : ''}
              </div>
              {col.description ? <p className="mt-1 whitespace-pre-line text-[var(--ink-2)]">{col.description}</p> : null}
            </div>
          </div>
          {first ? (
            <div className="flex flex-col gap-3 sm:flex-row md:shrink-0">
              {offlineOk ? (
                <PackButton
                  kind="collection"
                  refId={col.id}
                  title={col.name}
                  label={t(col.kind === 'folder' ? 'offline.save_folder' : 'offline.save_setlist')}
                  className="w-full sm:w-auto"
                />
              ) : null}
              <Button size="lg" icon={Play} onClick={() => navigate(hrefFor(first))} className="w-full md:w-auto">
                {t('library.open_first')}
              </Button>
            </div>
          ) : null}
        </Card>
        {first ? <InstallForOfflineBanner /> : null}

        {!items ? (
          <QueryStatus q={songsQ} />
        ) : items.length === 0 ? (
          <EmptyState icon={KindIcon} title={t('library.collection_empty')} />
        ) : (
          <Card className="overflow-hidden">
            <ol className="divide-y divide-[var(--line)]">
              {items.map((it, n) => (
                <SongRow
                  key={it.entry.entryId}
                  song={it.song}
                  to={hrefFor(it)}
                  note={it.entry.note}
                  action={offlineOk ? <SongOfflineAction songId={it.song.id} title={it.song.title} /> : undefined}
                  leading={
                    <span
                      className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[17px] font-bold text-[var(--accent)] tabular-nums"
                      aria-label={t('library.position', { n: n + 1 })}
                    >
                      {n + 1}
                    </span>
                  }
                />
              ))}
            </ol>
          </Card>
        )}
      </div>
      {isEditor ? <ReorderSheet collectionId={col.id} open={reordering} onClose={() => setReordering(false)} /> : null}
    </Page>
  );
}
